/**
 * Shared image preset persistence for ImageEditor and CanvasHeightEditor.
 * Prefers localStorage; falls back to IndexedDB when quota fails; keeps both in sync when possible.
 */

export const IMAGE_PRESETS_STORAGE_KEY = "imagePresets";
export const IMAGE_PRESETS_CHANGED_EVENT = "canvas-image-presets-changed";

const IMAGE_PRESETS_DB_NAME = "canvas_image_editor";
const IMAGE_PRESETS_DB_VERSION = 1;
const IMAGE_PRESETS_STORE_NAME = "kv";

export const parseImagePresets = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const getImagePresetsFromLocalStorage = () => {
  try {
    const raw = localStorage.getItem(IMAGE_PRESETS_STORAGE_KEY);
    return parseImagePresets(raw);
  } catch {
    return [];
  }
};

const openImagePresetsDb = () =>
  new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve(null);
      return;
    }
    const request = window.indexedDB.open(
      IMAGE_PRESETS_DB_NAME,
      IMAGE_PRESETS_DB_VERSION,
    );
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(IMAGE_PRESETS_STORE_NAME)) {
        db.createObjectStore(IMAGE_PRESETS_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const readImagePresetsFromIndexedDb = async () => {
  const db = await openImagePresetsDb();
  if (!db) return [];
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IMAGE_PRESETS_STORE_NAME, "readonly");
    const store = tx.objectStore(IMAGE_PRESETS_STORE_NAME);
    const request = store.get(IMAGE_PRESETS_STORAGE_KEY);
    request.onsuccess = () => {
      resolve(Array.isArray(request.result) ? request.result : []);
    };
    request.onerror = () => reject(request.error);
  }).finally(() => {
    db.close();
  });
};

export const writeImagePresetsToIndexedDb = async (presets) => {
  const db = await openImagePresetsDb();
  if (!db) return;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IMAGE_PRESETS_STORE_NAME, "readwrite");
    const store = tx.objectStore(IMAGE_PRESETS_STORE_NAME);
    store.put(presets, IMAGE_PRESETS_STORAGE_KEY);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  }).finally(() => {
    db.close();
  });
};

/** Same load order as ImageEditor: non-empty localStorage wins, else IndexedDB. */
export async function loadImagePresets() {
  const localPresets = getImagePresetsFromLocalStorage();
  if (localPresets.length > 0) return localPresets;
  try {
    return await readImagePresetsFromIndexedDb();
  } catch (error) {
    console.warn("Failed to load image presets from IndexedDB:", error);
    return [];
  }
}

/**
 * Save presets to localStorage and IndexedDB; notify listeners (same-tab + after IDB attempt).
 */
export function persistImagePresets(presets) {
  let localStorageSaved = false;
  try {
    localStorage.setItem(IMAGE_PRESETS_STORAGE_KEY, JSON.stringify(presets));
    localStorageSaved = true;
  } catch (error) {
    if (error.name === "QuotaExceededError") {
      console.warn(
        "LocalStorage quota exceeded. Falling back to IndexedDB for image presets.",
      );
    } else {
      console.warn(
        "Error saving image presets to localStorage. Falling back to IndexedDB:",
        error,
      );
    }
  }

  writeImagePresetsToIndexedDb(presets)
    .catch((error) => {
      if (!localStorageSaved) {
        console.error("Failed to save image presets:", error);
        alert("Failed to save image style.");
      } else {
        console.warn(
          "Saved image presets in localStorage, but IndexedDB backup failed:",
          error,
        );
      }
    })
    .finally(() => {
      window.dispatchEvent(new Event(IMAGE_PRESETS_CHANGED_EVENT));
    });
}
